import { useEffect, useState } from 'react';

import { fetchTestTabRecommended } from '@api/fetchers/goal/test-tab';
import type { RecommendedQuizzesResponse } from '@api/fetchers/goal/test-tab/types';

interface UseTestTabRecommendedParams {
  goalUid?: string;
  languageCode?: number;
  enabled?: boolean;
}

const useTestTabRecommended = ({
  goalUid,
  languageCode,
  enabled = true
}: UseTestTabRecommendedParams) => {
  const shouldFetch = Boolean(goalUid && enabled);
  const [data, setData] = useState<RecommendedQuizzesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!goalUid || !enabled) {
      setData(null);
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);

      try {
        const response = await fetchTestTabRecommended({
          goalUid,
          languageCode
        });

        if (!cancelled) {
          setData(response ?? null);
        }
      } catch {
        if (!cancelled) {
          setData(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [goalUid, languageCode, enabled]);

  return {
    data,
    isLoading: shouldFetch && isLoading
  };
};

export default useTestTabRecommended;
